/* Copyright (c) 2026 Hyegro Pty Ltd. All rights reserved. */

/**
 * v2 call console (docs/ui-v2/CALL_WRAP_UP.md). A view only: CallConsole owns
 * the state and handlers and renders this when the user is on the new look;
 * the classic view is unchanged.
 *
 * Left: the call (who, state, timer, live controls) and the wrap-up (outcome,
 * notes, who it was about, an optional follow-up task with a reminder). Right:
 * the caller's history (CustomerPanel). Dialling and presence live in the v2
 * calling bar along the bottom of every page, so they are not repeated here.
 */

import { useState } from "react";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import ButtonBase from "@mui/material/ButtonBase";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Collapse from "@mui/material/Collapse";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {
  ClipboardCheck,
  Grid3x3,
  History,
  Mic,
  MicOff,
  Phone,
  PhoneIncoming,
  PhoneOff,
  PhoneOutgoing,
  Search,
  Sparkles,
} from "lucide-react";
import { v2 as t, v2Tones } from "styles/tokens/v2";
import { CALL_STATE, CALL_STATE_META } from "telephony/callStates";
import { Dialpad } from "telephony/components";
import LinkRecordPanel from "../LinkRecordPanel";
import FollowUpFields from "../FollowUpFields";
import CustomerPanel from "./CustomerPanel";

const mmss = (sec) => {
  const s = Math.max(0, Math.floor(sec || 0));
  return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;
};

function Panel({ icon: Icon, title, children, action }) {
  return (
    <Card sx={{ p: 0, overflow: "hidden" }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 2.25, py: 1.5, borderBottom: `1px solid ${t.border}` }}>
        <Icon size={16} color={t.text2} aria-hidden="true" />
        <Typography component="h2" sx={{ fontSize: 15, fontWeight: 600, color: t.text, flex: 1 }}>{title}</Typography>
        {action}
      </Box>
      <Box sx={{ p: 2.25 }}>{children}</Box>
    </Card>
  );
}
Panel.propTypes = { icon: PropTypes.elementType.isRequired, title: PropTypes.string.isRequired, children: PropTypes.node, action: PropTypes.node };
Panel.defaultProps = { children: null, action: null };

function ControlButton({ icon: Icon, label, onClick, tone, active }) {
  const danger = tone === "danger";
  return (
    <ButtonBase
      onClick={onClick}
      aria-label={label}
      aria-pressed={active || undefined}
      sx={{
        flexDirection: "column", gap: 0.5, px: 1.5, py: 1, borderRadius: "10px", fontFamily: "inherit", minWidth: 76,
        color: danger ? "#FFFFFF" : active ? v2Tones.info.fg : t.text,
        bgcolor: danger ? "#C62828" : active ? v2Tones.info.bg : t.surface2,
        border: `1px solid ${danger ? "#C62828" : t.border}`,
        "&:hover": { bgcolor: danger ? "#A61F1F" : t.hover },
        "&:focus-visible": { outline: `2px solid ${t.primary}`, outlineOffset: 2 },
      }}
    >
      <Icon size={18} aria-hidden="true" />
      <Typography component="span" sx={{ fontSize: 12.5, fontWeight: 600, color: "inherit" }}>{label}</Typography>
    </ButtonBase>
  );
}
ControlButton.propTypes = { icon: PropTypes.elementType.isRequired, label: PropTypes.string.isRequired, onClick: PropTypes.func, tone: PropTypes.string, active: PropTypes.bool };
ControlButton.defaultProps = { onClick: undefined, tone: undefined, active: false };

export default function CallConsoleV2(props) {
  const {
    callState, activeCall, counterparty, resolvedName, durationSec, muted,
    onAccept, onReject, onHangUp, onToggleMute, onSendDigits,
    dispositions, dispositionId, onDispositionChange, notes, onNotesChange, onNotesBlur,
    onComplete, onSkipWrapUp, saving, onLinked,
    followUp, onFollowUpChange, users, currentUserId, followUpError, unattached,
    lookup, resolved, matches, onPickMatch, looking, onLookup, manualNumber, onManualNumberChange, recent,
  } = props;
  const [padOpen, setPadOpen] = useState(false);

  const isIdle = callState === CALL_STATE.IDLE;
  const isRinging = callState === CALL_STATE.RINGING;
  const isConnecting = callState === CALL_STATE.CONNECTING;
  const isLive = callState === CALL_STATE.CONNECTED || callState === CALL_STATE.ON_HOLD;
  const isWrapUp = callState === CALL_STATE.WRAP_UP;
  const meta = CALL_STATE_META[callState] || CALL_STATE_META[CALL_STATE.IDLE];
  const selected = dispositions.find((d) => d.id === dispositionId);
  const noteRequired = Boolean(selected?.requires_notes) && !notes.trim();
  const outbound = activeCall?.direction === "outbound";
  const DirIcon = outbound ? PhoneOutgoing : PhoneIncoming;

  return (
    <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "minmax(340px, 420px) minmax(0, 1fr)" }, gap: 2.5, alignItems: "start" }}>
      {/* ------------------------------------------------ call + wrap-up */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 2, position: { lg: "sticky" }, top: { lg: 16 } }}>
        {isIdle ? (
          <>
            <Panel icon={Search} title="Look up a number">
              <Box display="flex" gap={1}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder="Phone number"
                  value={manualNumber}
                  onChange={(e) => onManualNumberChange(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && onLookup(manualNumber.trim())}
                  inputProps={{ "aria-label": "Phone number to look up" }}
                />
                <Button variant="outlined" onClick={() => onLookup(manualNumber.trim())} sx={{ textTransform: "none", fontWeight: 600, color: t.primary, borderColor: t.border2 }}>
                  Search
                </Button>
              </Box>
              <Typography sx={{ fontSize: 12.5, color: t.text3, mt: 1 }}>
                Place calls from the calling bar at the bottom of the screen.
              </Typography>
            </Panel>
            <Panel icon={History} title="Recent calls">
              {recent.length === 0 ? (
                <Typography sx={{ fontSize: 13.5, color: t.text3 }}>No calls yet.</Typography>
              ) : recent.map((r) => {
                const num = r.direction === "outbound" ? r.to_address : r.from_address;
                const RIcon = r.direction === "outbound" ? PhoneOutgoing : PhoneIncoming;
                return (
                  <ButtonBase
                    key={r.id}
                    onClick={() => onLookup(num)}
                    sx={{ display: "flex", width: "100%", gap: 1.25, alignItems: "center", textAlign: "left", py: 0.9, px: 0.5, borderRadius: "6px", fontFamily: "inherit", "&:hover": { bgcolor: t.hover } }}
                  >
                    <RIcon size={15} color={t.text2} aria-hidden="true" />
                    <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: t.text, flex: 1 }}>{num}</Typography>
                    <Typography sx={{ fontSize: 12.5, color: t.text3 }}>{r.disposition_name || r.status}</Typography>
                  </ButtonBase>
                );
              })}
            </Panel>
          </>
        ) : (
          <Card sx={{ p: 2.25 }}>
            <Box display="flex" alignItems="center" gap={1.25}>
              <Box sx={{ width: 40, height: 40, borderRadius: "50%", display: "grid", placeItems: "center", bgcolor: v2Tones.info.bg, color: v2Tones.info.fg }}>
                <DirIcon size={18} aria-hidden="true" />
              </Box>
              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Typography sx={{ fontSize: 17, fontWeight: 700, color: t.text, wordBreak: "break-word" }}>{resolvedName || counterparty || "Caller"}</Typography>
                <Typography sx={{ fontSize: 13, color: t.text2 }}>{counterparty}</Typography>
              </Box>
              <Box sx={{ textAlign: "right" }}>
                <Box component="span" sx={{ display: "inline-block", fontSize: 12, fontWeight: 600, px: 1, py: 0.25, borderRadius: "999px", bgcolor: isWrapUp ? v2Tones.info.bg : isLive ? v2Tones.success.bg : v2Tones.neutral.bg, color: isWrapUp ? v2Tones.info.fg : isLive ? v2Tones.success.fg : v2Tones.neutral.fg }}>
                  {meta.label}
                </Box>
                {(isLive || isWrapUp) && (
                  <Typography sx={{ fontSize: 13, color: t.text2, mt: 0.5, fontVariantNumeric: "tabular-nums" }}>{mmss(durationSec)}</Typography>
                )}
              </Box>
            </Box>

            {activeCall?.ai_summary ? (
              <Box sx={{ mt: 1.75, p: 1.5, borderRadius: "10px", bgcolor: v2Tones.purple.bg, display: "flex", gap: 1 }}>
                <Sparkles size={16} color={v2Tones.purple.fg} aria-hidden="true" style={{ flexShrink: 0, marginTop: 2 }} />
                <Typography sx={{ fontSize: 13.5, color: t.text, whiteSpace: "pre-line" }}>{activeCall.ai_summary}</Typography>
              </Box>
            ) : null}

            {isRinging && (
              <Box display="flex" gap={1.25} mt={2}>
                <Button fullWidth variant="contained" disableElevation startIcon={<Phone size={16} />} onClick={onAccept} sx={{ textTransform: "none", fontWeight: 600, bgcolor: "#1B7F3B", color: "#FFFFFF !important", "&:hover": { bgcolor: "#166534" } }}>Accept</Button>
                <Button fullWidth variant="outlined" startIcon={<PhoneOff size={16} />} onClick={onReject} sx={{ textTransform: "none", fontWeight: 600, color: "#B42318", borderColor: "#F3B8B3" }}>Decline</Button>
              </Box>
            )}
            {isConnecting && (
              <Box display="flex" alignItems="center" gap={1.25} mt={2}>
                <CircularProgress size={16} />
                <Typography sx={{ fontSize: 13.5, color: t.text2 }}>Waiting for the other end to pick up.</Typography>
              </Box>
            )}
            {isLive && (
              <>
                <Box display="flex" justifyContent="space-between" gap={1} mt={2}>
                  <ControlButton icon={muted ? MicOff : Mic} label={muted ? "Unmute" : "Mute"} onClick={onToggleMute} active={muted} />
                  <ControlButton icon={Grid3x3} label="Keypad" onClick={() => setPadOpen((p) => !p)} active={padOpen} />
                  <ControlButton icon={PhoneOff} label="Hang up" onClick={onHangUp} tone="danger" />
                </Box>
                <Collapse in={padOpen}>
                  <Box mt={2}><Dialpad onKey={onSendDigits} showLetters={false} size="small" /></Box>
                </Collapse>
              </>
            )}
          </Card>
        )}

        {(isLive || isWrapUp) && (
          <Panel icon={ClipboardCheck} title={isWrapUp ? "Wrap-up" : "Notes"}>
            <Box display="flex" flexDirection="column" gap={1.75}>
              <TextField
                select
                size="small"
                label="Outcome"
                value={dispositionId}
                disabled={!isWrapUp}
                onChange={(e) => onDispositionChange(e.target.value)}
                helperText={!isWrapUp ? "Set the outcome once the call ends." : dispositions.length ? " " : "No outcomes are set up yet."}
              >
                <MenuItem value=""><em>Select an outcome</em></MenuItem>
                {dispositions.map((d) => <MenuItem key={d.id} value={d.id}>{d.name}</MenuItem>)}
              </TextField>
              <TextField
                multiline
                minRows={3}
                size="small"
                label="Notes"
                value={notes}
                onChange={(e) => onNotesChange(e.target.value)}
                onBlur={onNotesBlur}
                error={noteRequired && isWrapUp}
                helperText={selected?.requires_notes ? "This outcome requires a note." : isLive ? "Saved as you go." : " "}
              />

              <Box sx={{ borderTop: `1px solid ${t.border}`, pt: 1.75 }}>
                <Typography sx={{ fontSize: 13, fontWeight: 600, color: t.text2, mb: 1 }}>Who was this call about?</Typography>
                <LinkRecordPanel conversation={activeCall} onLinked={onLinked} disabled={!activeCall} />
                {unattached && isWrapUp && (
                  <Typography sx={{ fontSize: 12.5, color: v2Tones.warning.fg, mt: 1 }}>
                    Not attached to a record yet, so the outcome won&apos;t appear on anyone&apos;s timeline.
                  </Typography>
                )}
              </Box>

              {isWrapUp && (
                <Box sx={{ borderTop: `1px solid ${t.border}`, pt: 1.75 }}>
                  <FollowUpFields
                    value={followUp}
                    onChange={onFollowUpChange}
                    users={users}
                    currentUserId={currentUserId}
                    disabled={saving}
                    problem={followUpError}
                  />
                </Box>
              )}

              {isWrapUp && (
                <Box display="flex" flexDirection="column" gap={1}>
                  <Button
                    variant="contained"
                    disableElevation
                    disabled={saving || noteRequired || Boolean(followUpError)}
                    onClick={onComplete}
                    sx={{ textTransform: "none", fontWeight: 600, bgcolor: t.primary, color: `${t.onPrimary} !important`, "&:hover": { bgcolor: t.primaryDark } }}
                  >
                    {saving ? <CircularProgress size={18} sx={{ color: "#FFFFFF" }} /> : followUp.enabled ? "Save call and follow-up" : "Save & complete"}
                  </Button>
                  <Button variant="text" disabled={saving} onClick={onSkipWrapUp} sx={{ textTransform: "none", fontWeight: 600, color: t.primary }}>
                    Skip wrap-up
                  </Button>
                </Box>
              )}
            </Box>
          </Panel>
        )}
      </Box>

      {/* --------------------------------------------- customer context */}
      <Box>
        {looking ? (
          <Card sx={{ p: 4, display: "flex", justifyContent: "center" }}><CircularProgress size={24} /></Card>
        ) : lookup && matches.length > 1 && !resolved ? (
          <Panel icon={Search} title={`${matches.length} records share this number`}>
            <Typography sx={{ fontSize: 13.5, color: t.text2, mb: 1.25 }}>Choose who you are speaking to.</Typography>
            {matches.map((m) => (
              <ButtonBase
                key={`${m.type}-${m.id}`}
                onClick={() => onPickMatch(m)}
                sx={{ display: "flex", width: "100%", textAlign: "left", gap: 1, py: 1, px: 1.25, mb: 0.75, borderRadius: "8px", border: `1px solid ${t.border}`, fontFamily: "inherit", "&:hover": { bgcolor: t.hover } }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography sx={{ fontSize: 14, fontWeight: 600, color: t.text }}>{m.name}</Typography>
                  <Typography sx={{ fontSize: 12.5, color: t.text2 }}>{[m.type, m.subtitle].filter(Boolean).join(" · ")}{m.seen_before ? " · spoken before" : ""}</Typography>
                </Box>
              </ButtonBase>
            ))}
          </Panel>
        ) : (
          <CustomerPanel lookup={lookup} resolved={resolved} currentCallId={activeCall?.id || null} />
        )}
      </Box>
    </Box>
  );
}

CallConsoleV2.propTypes = {
  callState: PropTypes.string.isRequired,
  activeCall: PropTypes.object,
  counterparty: PropTypes.string,
  resolvedName: PropTypes.string,
  durationSec: PropTypes.number,
  muted: PropTypes.bool,
  onAccept: PropTypes.func.isRequired,
  onReject: PropTypes.func.isRequired,
  onHangUp: PropTypes.func.isRequired,
  onToggleMute: PropTypes.func.isRequired,
  onSendDigits: PropTypes.func.isRequired,
  dispositions: PropTypes.arrayOf(PropTypes.object).isRequired,
  dispositionId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onDispositionChange: PropTypes.func.isRequired,
  notes: PropTypes.string.isRequired,
  onNotesChange: PropTypes.func.isRequired,
  onNotesBlur: PropTypes.func,
  onComplete: PropTypes.func.isRequired,
  onSkipWrapUp: PropTypes.func.isRequired,
  saving: PropTypes.bool,
  onLinked: PropTypes.func,
  followUp: PropTypes.object.isRequired,
  onFollowUpChange: PropTypes.func.isRequired,
  users: PropTypes.arrayOf(PropTypes.object),
  currentUserId: PropTypes.number,
  followUpError: PropTypes.string,
  unattached: PropTypes.bool,
  lookup: PropTypes.object,
  resolved: PropTypes.object,
  matches: PropTypes.arrayOf(PropTypes.object),
  onPickMatch: PropTypes.func.isRequired,
  looking: PropTypes.bool,
  onLookup: PropTypes.func.isRequired,
  manualNumber: PropTypes.string,
  onManualNumberChange: PropTypes.func.isRequired,
  recent: PropTypes.arrayOf(PropTypes.object),
};
CallConsoleV2.defaultProps = {
  activeCall: null, counterparty: null, resolvedName: null, durationSec: 0, muted: false,
  dispositionId: "", onNotesBlur: undefined, saving: false, onLinked: undefined,
  users: [], currentUserId: null, followUpError: "", unattached: false,
  lookup: null, resolved: null, matches: [], looking: false, manualNumber: "", recent: [],
};
